<script setup>
import { computed } from 'vue';
import ChartBar from '@/components/charts/bar.vue';
import ComparisonChartDonut from '@/components/charts/comparisonDonut.vue';
import ChartDonut from '@/components/charts/donut.vue';
import ChartLine from '@/components/charts/line.vue';

const DEFAULT_FILL_COLORS = [
    '#0f172a',
    '#0ea5e9',
    '#22c55e',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
];
const COMPARISON_FILL_COLOR = '#bebebe';

const props = defineProps({
    chartType: {
        type: String,
        required: true,
    },
    payload: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    fillColors: {
        type: Array,
        required: false,
        default: () => [
            '#0f172a',
            '#0ea5e9',
            '#22c55e',
            '#f59e0b',
            '#ef4444',
            '#8b5cf6',
        ],
    },
    customColors: {
        type: Array,
        required: false,
        default: () => [],
    },
    donutCenterValueDisplay: {
        type: String,
        required: false,
        default: 'percentage',
        validator: (value) =>
            ['percentage', 'actual', 'percentage_and_actual'].includes(value),
    },
    emptyMessage: {
        type: String,
        required: false,
        default: 'No numeric values are available for this chart preview.',
    },
});

const previewData = computed(() => props.payload?.data ?? {});

const normalizeColors = (colors) => {
    if (!Array.isArray(colors)) {
        return [];
    }

    return colors
        .filter((color) => typeof color === 'string')
        .map((color) => color.trim())
        .filter((color) => color !== '');
};

const defaultFillColors = computed(() => {
    const configuredColors = normalizeColors(props.fillColors);

    return configuredColors.length > 0 ? configuredColors : DEFAULT_FILL_COLORS;
});

const customFillColors = computed(() => normalizeColors(props.customColors));

const toNumericValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumericLabel = (value) => {
    return toNumericValue(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6,
    });
};

const donutSeries = computed(() => {
    if (!Array.isArray(previewData.value?.series)) {
        return [];
    }

    return previewData.value.series.map((value) => toNumericValue(value));
});

const comparisonPercentage = computed(() => {
    if (
        props.chartType !== 'donut' ||
        props.payload?.chart?.comparison_settings?.enabled !== true
    ) {
        return null;
    }

    const primarySum = toNumericValue(props.payload?.summary?.value_sum);
    const comparisonSum = toNumericValue(
        props.payload?.summary?.comparison_value_sum
    );
    const combinedSum = primarySum + comparisonSum;

    if (combinedSum === 0) {
        return '0%';
    }

    return `${Number(((primarySum / combinedSum) * 100).toFixed(1))}%`;
});

const comparisonActualValue = computed(() => {
    if (
        props.chartType !== 'donut' ||
        props.payload?.chart?.comparison_settings?.enabled !== true
    ) {
        return null;
    }

    const primarySum = toNumericValue(props.payload?.summary?.value_sum);
    const comparisonSum = toNumericValue(
        props.payload?.summary?.comparison_value_sum
    );

    return `${formatNumericLabel(primarySum)} / ${formatNumericLabel(
        primarySum + comparisonSum
    )}`;
});

const comparisonCenterLabel = computed(() => {
    if (props.donutCenterValueDisplay === 'actual') {
        return comparisonActualValue.value;
    }

    return comparisonPercentage.value;
});

const groupedSeries = computed(() => {
    if (!Array.isArray(previewData.value?.series)) {
        return [];
    }

    return previewData.value.series
        .filter((series) => series && Array.isArray(series.data))
        .map((series, index) => ({
            name:
                typeof series.name === 'string' && series.name.trim() !== ''
                    ? series.name
                    : `Series ${index + 1}`,
            data: series.data.map((value) => toNumericValue(value)),
        }))
        .filter((series) => series.data.length > 0);
});

const labels = computed(() => {
    if (
        Array.isArray(previewData.value?.labels) &&
        previewData.value.labels.length > 0
    ) {
        return previewData.value.labels.map((label) => String(label));
    }

    const fallbackCount =
        props.chartType === 'donut'
            ? donutSeries.value.length
            : groupedSeries.value[0]?.data?.length ?? 0;

    return Array.from(
        { length: fallbackCount },
        (_, index) => `Item ${index + 1}`
    );
});

const entryCount = computed(() => {
    if (props.chartType === 'donut') {
        return donutSeries.value.length;
    }

    return groupedSeries.value[0]?.data?.length ?? labels.value.length;
});

const isComparisonBarChart = computed(() => {
    return (
        props.chartType === 'bar' &&
        groupedSeries.value.length > 1 &&
        props.payload?.chart?.comparison_settings?.enabled === true
    );
});

const isComparisonDonutChart = computed(() => {
    return (
        props.chartType === 'donut' &&
        props.payload?.chart?.comparison_settings?.enabled === true
    );
});

const categoryFillColors = computed(() => {
    const palette =
        customFillColors.value.length > 0
            ? customFillColors.value
            : defaultFillColors.value;
    const colorCount = Math.max(
        entryCount.value,
        labels.value.length,
        palette.length
    );

    return Array.from(
        { length: colorCount },
        (_, index) => palette[index % palette.length]
    );
});

const chartFillColors = computed(() => {
    const colors = [...categoryFillColors.value];

    if (isComparisonBarChart.value) {
        colors[1] = COMPARISON_FILL_COLOR;
    }

    return colors;
});

const barSeries = computed(() => {
    if (!isComparisonBarChart.value) {
        return groupedSeries.value;
    }

    return groupedSeries.value.map((series, seriesIndex) => ({
        name: series.name,
        data: series.data.map((value, dataIndex) => ({
            x: labels.value[dataIndex] ?? `Item ${dataIndex + 1}`,
            y: value,
            fillColor:
                seriesIndex === 1
                    ? COMPARISON_FILL_COLOR
                    : categoryFillColors.value[
                          dataIndex % categoryFillColors.value.length
                      ],
        })),
    }));
});

const isTableType = computed(() => props.chartType === 'table');

const hasRenderableData = computed(() => {
    if (props.chartType === 'donut') {
        return labels.value.length > 0 && donutSeries.value.length > 0;
    }

    if (props.chartType === 'bar' || props.chartType === 'line') {
        return labels.value.length > 0 && groupedSeries.value.length > 0;
    }

    return false;
});

const chartRenderKey = computed(() => {
    const seriesHash =
        props.chartType === 'donut'
            ? donutSeries.value.join('|')
            : groupedSeries.value
                  .map((series) => `${series.name}:${series.data.join('|')}`)
                  .join('||');
    const colorHash = isComparisonBarChart.value
        ? categoryFillColors.value.join('|')
        : chartFillColors.value.join('|');

    return `${props.chartType}:${labels.value.join(
        '|'
    )}:${seriesHash}:${colorHash}`;
});
</script>

<template>
    <div>
        <p v-if="isTableType" class="text-sm text-gray-500">
            Table charts do not include a visual graph.
        </p>

        <p v-else-if="!hasRenderableData" class="text-sm text-gray-500">
            {{ emptyMessage }}
        </p>

        <comparison-chart-donut
            v-else-if="isComparisonDonutChart"
            :key="chartRenderKey"
            :labels="labels"
            :series="donutSeries"
            :fill-colors="chartFillColors"
        >
            <template #label>
                <span
                    v-if="comparisonCenterLabel !== null"
                    :style="{ color: chartFillColors[0] }"
                >
                    {{ comparisonCenterLabel }}
                </span>
            </template>
        </comparison-chart-donut>

        <chart-donut
            v-else-if="chartType === 'donut'"
            :key="chartRenderKey"
            :labels="labels"
            :series="donutSeries"
            :fill-colors="chartFillColors"
        />

        <chart-bar
            v-else-if="chartType === 'bar'"
            :key="chartRenderKey"
            :labels="labels"
            :series="barSeries"
            :fill-colors="chartFillColors"
            :distributed-colors="groupedSeries.length === 1"
            :show-tooltip="true"
            :show-legend="groupedSeries.length > 1"
        />

        <chart-line
            v-else-if="chartType === 'line'"
            :key="chartRenderKey"
            :labels="labels"
            :series="groupedSeries"
            :fill-colors="chartFillColors"
            :point-colors="chartFillColors"
            :show-tooltip="true"
            :show-legend="groupedSeries.length > 1"
        />

        <p v-else class="text-sm text-gray-500">
            Unsupported chart type for preview.
        </p>
    </div>
</template>
